import { useLoad } from "@redotech/react-util/load";
import { StyleRoot } from "@redotech/react-util/style";
import { LOYALTY_PAGE_MAX_WIDTH } from "@redotech/redo-loyalty-common/loyalty-page-builder/definition";
import { defaultBrandKit } from "@redotech/redo-model/brand-kit";
import { WidgetAutocheck } from "@redotech/redo-model/widget";
import { Flex } from "@redotech/redo-web/flex";
import { BlockContent } from "@redotech/redo-web/loyalty-page-builder/blocks/index";
import {
  LoyaltyPageContext,
  LoyaltyPageEnvironment,
} from "@redotech/redo-web/loyalty-page-builder/context/loyalty-page-context";
import { Text } from "@redotech/redo-web/text";
import { completeEarningEventForLoyaltyCustomer } from "@redotech/shopify-sdk/rpc/schema/loyalty/complete-earning-event-for-loyalty-customer";
import { getLoyaltyConfiguration } from "@redotech/shopify-sdk/rpc/schema/loyalty/get-loyalty-configuration";
import { getLoyaltyCustomer } from "@redotech/shopify-sdk/rpc/schema/loyalty/get-loyalty-customer";
import { getLoyaltyPromotions } from "@redotech/shopify-sdk/rpc/schema/loyalty/get-loyalty-promotions";
import { updateCustomerBirthday } from "@redotech/shopify-sdk/rpc/schema/loyalty/update-customer-birthday";
import { useContext } from "react";
import { createRoot } from "react-dom/client";
import {
  RedoShopifyRpcClientContext,
  RedoShopifyRpcClientContextProvider,
} from "../client/redo-shopify-rpc-client";
import { log } from "../monitoring/log";
import { RedoRoot } from "../root";
import STYLE_MANAGER from "../style-manager";

export function initialize(autocheck: WidgetAutocheck) {
  log("Initializing Loyalty page customer view");

  const container = document.getElementById("redo-loyalty-page");
  if (!container) {
    log("No loyalty page container found");
    return;
  }

  log("Found Loyalty page container");

  // Shopify applies display: none to empty divs
  container.style.display = "unset";

  const root = createRoot(container);
  root.render(
    <StyleRoot container={container} styleManager={STYLE_MANAGER}>
      <RedoRoot
        autocheck={autocheck}
        extensionShopifyClient={undefined}
        storeUrl={
          globalThis.redoStorefront?.storeUrl ??
          globalThis.Shopify?.shop ??
          globalThis.Shopify?.Checkout?.apiHost
        }
      >
        <RedoShopifyRpcClientContextProvider
          baseUrl={
            globalThis.redoStorefront?.env?.REDO_SHOPIFY_SERVER_URL ??
            process.env.REDO_SHOPIFY_SERVER_URL!
          }
          storeUrl={
            globalThis.Shopify?.shop ?? globalThis.Shopify?.Checkout?.apiHost
          }
          widgetId={globalThis.redoWidgetId}
        >
          <RedoLoyaltyPageCustomerView autocheck={autocheck} />
        </RedoShopifyRpcClientContextProvider>
      </RedoRoot>
    </StyleRoot>,
  );
}

function RedoLoyaltyPageCustomerView({
  autocheck,
}: {
  autocheck: WidgetAutocheck;
}) {
  const rpcClient = useContext(RedoShopifyRpcClientContext);

  const loyaltyConfigurationResult = useLoad(() => {
    if (!rpcClient) {
      throw new Error("RPC client not available");
    }
    return rpcClient(getLoyaltyConfiguration, {});
  }, [rpcClient]);

  const loyaltyPromotionsResult = useLoad(() => {
    if (!rpcClient) {
      throw new Error("RPC client not available");
    }
    return rpcClient(getLoyaltyPromotions, {});
  }, [rpcClient]);

  const loyaltyCustomerResult = useLoad(() => {
    if (!rpcClient) {
      throw new Error("RPC client not available");
    }

    const customerId = (
      globalThis as any
    ).ShopifyAnalytics?.meta?.page?.customerId?.toString();

    if (customerId) {
      return rpcClient(getLoyaltyCustomer, {
        customerIdentifier: { shopifyCustomerId: customerId },
      });
    }

    const shopifyCustomer = (globalThis as any).Shopify?.customer;

    if (shopifyCustomer) {
      if (shopifyCustomer.id) {
        return rpcClient(getLoyaltyCustomer, {
          customerIdentifier: { shopifyCustomerId: shopifyCustomer.id },
        });
      } else if (shopifyCustomer.email) {
        return rpcClient(getLoyaltyCustomer, {
          customerIdentifier: { email: shopifyCustomer.email },
        });
      } else if (shopifyCustomer.phone) {
        return rpcClient(getLoyaltyCustomer, {
          customerIdentifier: { phoneNumber: shopifyCustomer.phone },
        });
      }
    }

    return Promise.resolve({ loyaltyCustomer: null });
  }, [rpcClient]);

  const brandKit = autocheck.brandKit ?? defaultBrandKit;

  if (
    loyaltyConfigurationResult.pending ||
    loyaltyPromotionsResult.pending ||
    loyaltyCustomerResult.pending
  ) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{
          width: LOYALTY_PAGE_MAX_WIDTH,
          maxWidth: "100%",
          margin: "0 auto",
          minHeight: "200px",
        }}
      >
        <Text fontSize="lg">Loading...</Text>
      </Flex>
    );
  }

  if (
    loyaltyConfigurationResult.error ||
    loyaltyPromotionsResult.error ||
    loyaltyCustomerResult.error
  ) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{
          width: LOYALTY_PAGE_MAX_WIDTH,
          maxWidth: "100%",
          margin: "0 auto",
          minHeight: "200px",
        }}
      >
        <Text fontSize="lg" style={{ color: "#dc3545" }}>
          Failed to load loyalty data
        </Text>
      </Flex>
    );
  }

  const loyaltyConfiguration =
    loyaltyConfigurationResult.value?.loyaltyConfiguration;
  const loyaltyPromotions =
    loyaltyPromotionsResult.value?.loyaltyPromotions || [];
  const loyaltyEarningRules = loyaltyConfiguration?.earningRules || [];
  const customer = loyaltyCustomerResult.value?.loyaltyCustomer || null;

  return (
    <LoyaltyPageContext.Provider
      value={{
        environment: LoyaltyPageEnvironment.STOREFRONT,
        customer,
        pageSettings: loyaltyConfiguration?.loyaltyPage,
        teamSettings: { brandKit },
        loyaltyEarningRules,
        loyaltyPromotions,
        completeEarningRule: async (earningRule) => {
          if (!customer) {
            return { success: false };
          }

          const result = await rpcClient?.(
            completeEarningEventForLoyaltyCustomer,
            { customerId: customer._id, completedEarningRule: earningRule },
          );

          return { success: result?.success ?? false };
        },
        updateBirthday: async (birthday) => {
          if (!customer) {
            return { success: false };
          }

          const result = await rpcClient?.(updateCustomerBirthday, {
            customerId: customer._id,
            birthday,
          });

          return { success: result?.success ?? false };
        },
        supportingProductsStatus: { reviewsEnabled: autocheck.reviews.enabled },
      }}
    >
      <Flex
        bgColor="primary"
        dir="column"
        gap="none"
        style={{
          width: LOYALTY_PAGE_MAX_WIDTH,
          maxWidth: "100%",
          margin: "0 auto",
        }}
      >
        {loyaltyConfiguration?.loyaltyPage?.blocks.map((block, index) => (
          <BlockContent block={block} key={block.id || index} />
        ))}
      </Flex>
    </LoyaltyPageContext.Provider>
  );
}
